import { ChangelogService } from '../api/services/ChangelogService.js';
import { FeedbackService } from '../api/services/FeedbackService.js';
import { HelpService } from '../api/services/HelpService.js';
import { MessengerService } from '../api/services/MessengerService.js';
import { SurveyService } from '../api/services/SurveyService.js';
import { BaseAPIService } from './BaseAPIService.js';

export class APIService extends BaseAPIService {
	constructor(config = {}) {
		super(config);

		this.feedback = new FeedbackService(this);
		this.survey = new SurveyService(this);
		this.messenger = new MessengerService(this);
		this.help = new HelpService(this);
		this.changelog = new ChangelogService(this);
	}

	async submitFeedback(data) {
		return this.feedback.submitFeedback(data);
	}

	async getActiveSurveys(context) {
		return this.survey.getActiveSurveys(context);
	}

	async submitSurveyResponse(surveyId, responseData) {
		return this.survey.submitSurveyResponse(surveyId, responseData);
	}

	async dismissSurvey(surveyId) {
		return this.survey.dismissSurvey(surveyId);
	}

	async getMessengerSettings() {
		return this.messenger.getMessengerSettings();
	}

	async checkAgentsOnline() {
		if (!this.isSessionValid()) {
			await this.init();
		}

		if (this.mock) {
			return {
				status: true,
				data: {
					agents_online: true,
					online_count: 2,
					response_time: 'Usually replies within a few minutes',
					available_agents: [
						{ full_name: 'Sarah', picture: '' },
						{ full_name: 'Tom', picture: '' },
					],
				},
			};
		}

		return this._makeRequest('/widget/messenger/agents/online', {
			method: 'GET',
			headers: { Authorization: `Bearer ${this.sessionToken}` },
		});
	}

	async getConversations(options) {
		return this.messenger.getConversations(options);
	}

	async getConversation(conversationId) {
		return this.messenger.getConversation(conversationId);
	}

	async getMessages(conversationId, options) {
		return this.messenger.getMessages(conversationId, options);
	}

	async startConversation(data) {
		if (!this.isSessionValid()) {
			console.log(
				'[APIService] startConversation: session invalid, calling init...'
			);
			try {
				await this.init();
				console.log(
					'[APIService] startConversation: init result, token:',
					this.sessionToken ? 'set' : 'null'
				);
			} catch (initError) {
				console.error(
					'[APIService] startConversation: init failed:',
					initError.message
				);
				throw initError;
			}
		}

		if (!this.sessionToken) {
			console.error(
				'[APIService] startConversation: no session token after init'
			);
			throw new APIError(401, 'No valid session token available');
		}

		console.log(
			'[APIService] startConversation: sending to',
			`${this.baseURL}/widget/messenger/conversations`,
			'mock:',
			this.mock
		);

		if (this.mock) {
			await new Promise((resolve) => setTimeout(resolve, 300));
			const newConv = {
				id: 'conv_' + Date.now(),
				subject: data.subject || 'New conversation',
				status: 'open',
				last_message_at: new Date().toISOString(),
				created_at: new Date().toISOString(),
				messages: [
					{
						id: 'msg_' + Date.now(),
						content: data.message,
						sender_type: 'customer',
						created_at: new Date().toISOString(),
					},
				],
			};
			MOCK_CONVERSATIONS.unshift(newConv);
			MOCK_MESSAGES[newConv.id] = newConv.messages;
			return { status: true, data: newConv };
		}

		return this._makeRequest('/widget/messenger/conversations', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${this.sessionToken}`,
			},
			body: JSON.stringify({
				message: data.message,
				subject: data.subject || '',
			}),
		});
	}

	async sendMessage(conversationId, data) {
		if (!this.isSessionValid()) {
			await this.init();
		}

		if (this.mock) {
			await new Promise((resolve) => setTimeout(resolve, 200));
			const newMessage = {
				id: 'msg_' + Date.now(),
				content: data.content,
				attachments: data.attachments || [],
				sender_type: 'customer',
				created_at: new Date().toISOString(),
			};
			if (!MOCK_MESSAGES[conversationId]) {
				MOCK_MESSAGES[conversationId] = [];
			}
			MOCK_MESSAGES[conversationId].push(newMessage);
			return { status: true, data: newMessage };
		}

		const payload = { content: data.content };
		if (data.attachments && data.attachments.length > 0) {
			payload.attachments = data.attachments;
		}

		return this._makeRequest(
			`/widget/messenger/conversations/${conversationId}/messages`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${this.sessionToken}`,
				},
				body: JSON.stringify(payload),
			}
		);
	}

	/**
	 * Upload a file to CDN via widget endpoint
	 * @param {string} base64Data - Base64 encoded file data (with or without data URI prefix)
	 * @param {string} filename - Original filename
	 * @returns {Promise<Object>} Response with url
	 */
	async uploadFile(base64Data, filename) {
		if (!this.isSessionValid()) {
			await this.init();
		}

		if (this.mock) {
			await new Promise((resolve) => setTimeout(resolve, 300));
			return { status: true, url: `https://mock-cdn.example.com/${filename}` };
		}

		return this._makeRequest('/widget/messenger/upload', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${this.sessionToken}`,
			},
			body: JSON.stringify({ file: base64Data, filename }),
		});
	}

	async sendTypingIndicator(conversationId, isTyping) {
		return this.messenger.sendTypingIndicator(conversationId, isTyping);
	}

	async markConversationAsRead(conversationId) {
		return this.messenger.markConversationAsRead(conversationId);
	}

	async getUnreadCount() {
		return this.messenger.getUnreadCount();
	}

	async submitRating(conversationId, data) {
		return this.messenger.submitRating(conversationId, data);
	}

	async identifyContact(data) {
		return this.messenger.identifyContact(data);
	}

	async getHelpCollections(options) {
		return this.help.getHelpCollections(options);
	}

	async searchHelpArticles(query, options) {
		return this.help.searchHelpArticles(query, options);
	}

	async getChangelogs(options) {
		return this.changelog.getChangelogs(options);
	}

	_loadStoredSession() {
		if (typeof localStorage === 'undefined') return false;

		try {
			const stored = localStorage.getItem('feedbackSDK_session');
			if (!stored) return false;

			const sessionData = JSON.parse(stored);

			// Invalidate mock tokens when not in mock mode (and vice versa)
			const isMockToken =
				sessionData.token && sessionData.token.startsWith('mock_');
			if (isMockToken !== this.mock) {
				localStorage.removeItem('feedbackSDK_session');
				return false;
			}

			this.sessionToken = sessionData.token;
			this.sessionExpiry = new Date(sessionData.expiry);

			return this.isSessionValid();
		} catch (error) {
			return false;
		}
	}

	async _makeRequest(endpoint, options = {}) {
		const url = `${this.baseURL}${endpoint}`;

		try {
			const response = await fetch(url, options);

			if (!response.ok) {
				let errorMessage = `HTTP ${response.status}`;
				let responseData = null;

				try {
					responseData = await response.json();
					errorMessage =
						responseData.message || responseData.error || errorMessage;
				} catch (e) {
					errorMessage = (await response.text()) || errorMessage;
				}

				throw new APIError(response.status, errorMessage, responseData);
			}

			const contentType = response.headers.get('content-type');
			if (contentType && contentType.includes('application/json')) {
				return await response.json();
			}

			return await response.text();
		} catch (error) {
			if (error instanceof APIError) {
				throw error;
			}
			throw new APIError(0, error.message, null);
		}
	}
}
